import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  RefreshCw, 
  Search, 
  Building2,
  Loader2,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Eye,
  GripVertical,
  ArrowRight,
  ArrowUpDown,
  Clock,
  User,
  Users,
  FileText,
  MessageCircle,
  Paperclip,
  Database,
  ExternalLink,
  TrendingUp,
  Banknote,
  Filter,
  X,
  AlertCircle,
  Wifi,
  WifiOff,
  Check,
  Bell,
  Activity,
  SlidersHorizontal,
  ShieldCheck,
  Globe,
  UserCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Zap,
  PlayCircle
} from "lucide-react";
import { toast } from "sonner";
import { 
  getWorkflowSnapshot,
  getWorkflowGroups, 
  getWorkflowItems, 
  syncWorkflowItems,
  getMondayItemUpdates,
  moveWorkflowItem,
  updateWorkflowItemStatus,
  postWorkflowItemUpdate,
  getWorkflowStatuses,
  runUnderwritingFiltering,
  runFullUnderwritingAssessment,
  checkDigitalFootprint,
  checkDirectorNetwork,
  getUnderwritingStatus
} from "@/lib/api";
import { useWorkflowWebSocket } from "@/hooks/useWorkflowWebSocket";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import UnderwritingPanel from "@/components/UnderwritingPanel";

// Group colors matching Monday.com
const GROUP_COLORS = {
  "new_group64800": "#0073ea",     // Application Stage
  "new_group__1": "#fdab3d",       // Processing
  "topics": "#00c875",             // For Underwriting
  "new_group23688__1": "#e2445c",  // Final UW Checks
  "new_group": "#a25ddc",          // Conditional Offer
  "new_group1663": "#ff642e",      // Info Requested
  "new_group1807": "#c4c4c4",      // Hold
  "new_group4493": "#037f4c",      // Offer Accepted
  "new_group_mkmkc789": "#ff158a", // Ready for Doc's
  "new_group55967__1": "#c4b4fc",  // Funded
  "new_group29596__1": "#e8697d",  // Offer Rejected - NB
  "group_mknh1sfx": "#9d50dd",     // Offer Rejected - TU
  "declined_finished_mkmqjztn": "#808080", // Declined/Finished
};

// Groups to hide from Applications Workflow (shown in Credit Approved Deals page)
const HIDDEN_GROUPS = ["new_group_mkmkc789", "new_group55967__1"];

// AI Opinion color mapping
const AI_OPINION_COLORS = {
  "InstantDecline": { bg: "bg-red-100", text: "text-red-700", label: "Decline" },
  "High": { bg: "bg-green-100", text: "text-green-700", label: "High" },
  "Medium": { bg: "bg-yellow-100", text: "text-yellow-700", label: "Medium" },
  "Low": { bg: "bg-orange-100", text: "text-orange-700", label: "Low" },
  "Review": { bg: "bg-blue-100", text: "text-blue-700", label: "Review" },
};

// Status colors matching Monday.com style
const STATUS_COLORS = {
  "Application": "#fdab3d",
  "UW": "#0073ea",
  "TopUp - UW": "#9d50dd",
  "Fast Track UW": "#ff158a",
  "DM - Review": "#579bfc",
  "Francis - Review": "#7f5347",
  "Merly Review": "#a1a1a1",
  "Bella Review": "#bda58b",
  "UW - Query": "#0086c0",
  "Processing": "#00c875",
  "Hold": "#9d50dd",
  "Declined": "#e2445c",
  "Declined - Pending": "#9d50dd",
  "For Revalidation": "#a25ddc",
  "UW - IR": "#00d2d2",
  "UW - Conditional Offer": "#00d2d2",
  "Info Requested": "#fdab3d",
  "1st Chase -IR": "#00d2d2",
  "2nd Chase -IR": "#00d2d2",
  "3rd Chase -IR": "#00d2d2",
  "Conditional Offer": "#ff158a",
  "1st Chase - Offer": "#037f4c",
  "2nd Chase - Offer": "#037f4c",
  "3rd Chase - Offer": "#037f4c",
  "Offer Accepted": "#037f4c",
  "Final Checks": "#175a63",
  "Raise Doc's": "#ff642e",
  "Doc's Out": "#ff642e",
  "Ready for Offer": "#cab641",
  "Ready To Fund": "#037f4c",
  "Offer Rejected - NB": "#fdab3d",
  "Offer Rejected - TU": "#00c875",
  "Funded": "#c4b4fc",
};

// Categorized status options for dropdown
const STATUS_CATEGORIES = {
  "Popular": ["Processing", "Application", "Hold", "Declined"],
  "Underwriting": ["UW", "UW - IR", "UW - Query", "UW - Conditional Offer", "TopUp - UW", "Fast Track UW", "For Revalidation"],
  "Review": ["DM - Review", "Francis - Review", "Merly Review", "Bella Review"],
  "Info Request": ["Info Requested", "1st Chase -IR", "2nd Chase -IR", "3rd Chase -IR"],
  "Offers": ["Conditional Offer", "1st Chase - Offer", "2nd Chase - Offer", "3rd Chase - Offer", "Offer Accepted", "Ready for Offer"],
  "Final Stage": ["Final Checks", "Raise Doc's", "Doc's Out", "Ready To Fund", "Funded"],
  "Declined/Rejected": ["Declined", "Declined - Pending", "Offer Rejected - NB", "Offer Rejected - TU"]
};

// Get status color (hex) for a status label
const getStatusColor = (status) => {
  return STATUS_COLORS[status] || "#c4c4c4";
};

export default function ApplicationsWorkflow() {
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState({});
  // `loading` defaults to FALSE — we no longer auto-load on mount, so the
  // page should render the empty-state gate immediately rather than flashing
  // a spinner. Becomes true only while loadWorkflowData() is in flight.
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [itemUpdates, setItemUpdates] = useState([]);
  const [itemColumns, setItemColumns] = useState({});
  const [updatesLoading, setUpdatesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [stats, setStats] = useState({ totalItems: 0, totalValue: 0 });
  
  // Action states
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [movingItem, setMovingItem] = useState(false);
  const [newUpdateText, setNewUpdateText] = useState("");
  const [postingUpdate, setPostingUpdate] = useState(false);
  const [inlineUpdatingItemId, setInlineUpdatingItemId] = useState(null);
  
  // Activity feed state
  const [activityFeed, setActivityFeed] = useState([]);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [unreadActivityCount, setUnreadActivityCount] = useState(0);
  
  // Underwriting state
  const [underwritingLoading, setUnderwritingLoading] = useState(false);
  const [filteringResult, setFilteringResult] = useState(null);
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [digitalFootprintResult, setDigitalFootprintResult] = useState(null);
  const [directorNetworkResult, setDirectorNetworkResult] = useState(null);
  
  // Group filters and sorting state
  const [groupFilters, setGroupFilters] = useState({}); // { groupId: { column: filterValue } }
  const [groupSort, setGroupSort] = useState({}); // { groupId: { column: 'asc' | 'desc' } }
  const [showFilters, setShowFilters] = useState({}); // { groupId: true/false }

  // Update group filter
  const updateGroupFilter = (groupId, column, value) => {
    setGroupFilters(prev => ({
      ...prev,
      [groupId]: {
        ...(prev[groupId] || {}),
        [column]: value
      }
    }));
  };

  // Clear all filters for a group
  const clearGroupFilters = (groupId) => {
    setGroupFilters(prev => ({
      ...prev,
      [groupId]: {}
    }));
  };

  // Toggle sort for a group column
  const toggleGroupSort = (groupId, column) => {
    setGroupSort(prev => {
      const currentSort = prev[groupId]?.column === column ? prev[groupId]?.direction : null;
      let newDirection;
      if (!currentSort) newDirection = 'asc';
      else if (currentSort === 'asc') newDirection = 'desc';
      else newDirection = null;
      
      return {
        ...prev,
        [groupId]: newDirection ? { column, direction: newDirection } : null
      };
    });
  };

  // Filter and sort items for a group
  const getFilteredSortedItems = (groupId, groupItems) => {
    let result = [...groupItems];
    
    // Apply global search first
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => 
        item.name?.toLowerCase().includes(query) ||
        item.sf_number?.toLowerCase().includes(query) ||
        item.broker_name?.toLowerCase().includes(query) ||
        item.status?.toLowerCase().includes(query)
      );
    }
    
    // Apply group-specific filters
    const filters = groupFilters[groupId] || {};
    Object.entries(filters).forEach(([column, filterValue]) => {
      if (!filterValue) return;
      const value = filterValue.toLowerCase();
      result = result.filter(item => {
        switch (column) {
          case 'name': return item.name?.toLowerCase().includes(value);
          case 'status': return item.status?.toLowerCase().includes(value);
          case 'ai_opinion': return item.ai_opinion?.toLowerCase().includes(value);
          case 'broker_name': return item.broker_name?.toLowerCase().includes(value);
          case 'contact_name': return item.contact_name?.toLowerCase().includes(value);
          case 'account_manager': return item.account_manager?.toLowerCase().includes(value);
          default: return true;
        }
      });
    });
    
    // Apply sorting
    const sort = groupSort[groupId];
    if (sort) {
      result.sort((a, b) => {
        let aVal, bVal;
        switch (sort.column) {
          case 'name': aVal = a.name || ''; bVal = b.name || ''; break;
          case 'date_created': aVal = a.date_created || ''; bVal = b.date_created || ''; break;
          case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
          case 'ai_opinion': aVal = a.ai_opinion || ''; bVal = b.ai_opinion || ''; break;
          case 'broker_name': aVal = a.broker_name || ''; bVal = b.broker_name || ''; break;
          case 'loan_amount': aVal = a.loan_amount || 0; bVal = b.loan_amount || 0; break;
          case 'outstanding_balance': aVal = a.outstanding_balance || 0; bVal = b.outstanding_balance || 0; break;
          default: return 0;
        }
        
        if (typeof aVal === 'number') {
          return sort.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        return sort.direction === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      });
    }
    
    return result;
  };

  // Add activity to feed (max 50 items)
  const addActivity = useCallback((activity) => {
    setActivityFeed(prev => {
      const newFeed = [{
        id: Date.now(),
        timestamp: new Date().toISOString(),
        ...activity
      }, ...prev].slice(0, 50);
      return newFeed;
    });
    setUnreadActivityCount(prev => prev + 1);
  }, []);

  // Recalculate stats from items
  const recalculateStats = useCallback((itemsData) => {
    let totalItems = 0;
    let totalValue = 0;
    Object.values(itemsData).forEach(groupItems => {
      totalItems += groupItems.length;
      totalValue += groupItems.reduce((sum, item) => sum + (item.loan_amount || 0), 0);
    });
    setStats({ totalItems, totalValue });
  }, []);

  // Last time the displayed data was updated (from WS or refresh).
  // Declared above handleWebSocketUpdate so the callback's closure
  // captures the setter correctly.
  const [lastDataUpdate, setLastDataUpdate] = useState(null);

  // Handle real-time WebSocket updates
  const handleWebSocketUpdate = useCallback((data) => {
    console.log("🔄 WebSocket update received:", data);
    
    if (data.type === "item_updated" && data.data?.item) {
      const updatedItem = data.data.item;
      const newGroupId = updatedItem.group_id;
      const newGroupName = data.data.group_name || newGroupId;
      
      console.log(`📦 Item ${updatedItem.monday_item_id} (${updatedItem.name}) -> Group: ${newGroupName}`);
      
      let oldGroupName = null;
      
      // Update the item in state and move between groups
      setItems(prevItems => {
        const newItems = { ...prevItems };
        let oldGroupId = null;
        
        // Find and remove item from its current group
        Object.keys(newItems).forEach(gId => {
          const idx = (newItems[gId] || []).findIndex(
            item => item.monday_item_id === updatedItem.monday_item_id
          );
          if (idx >= 0) {
            oldGroupId = gId;
            // Get old group name from the item before removing
            const oldItem = newItems[gId][idx];
            oldGroupName = oldItem?.group_name || gId;
            newItems[gId] = newItems[gId].filter(
              item => item.monday_item_id !== updatedItem.monday_item_id
            );
          }
        });
        
        // Add to new group
        if (!newItems[newGroupId]) {
          newItems[newGroupId] = [];
        }
        newItems[newGroupId] = [updatedItem, ...newItems[newGroupId]];
        
        console.log(`✅ Moved item from ${oldGroupId || 'unknown'} to ${newGroupId}`);
        
        // Recalculate stats after state update
        setTimeout(() => recalculateStats(newItems), 0);
        
        return newItems;
      });
      
      // Add to activity feed instead of showing toast
      addActivity({
        type: 'item_updated',
        itemId: updatedItem.monday_item_id,
        sfNumber: updatedItem.sf_number,
        itemName: updatedItem.name,
        status: updatedItem.status,
        fromGroup: oldGroupName,
        toGroup: newGroupName,
        source: 'monday' // indicates this came from Monday.com
      });
      setLastDataUpdate(new Date());
    }
  }, [recalculateStats, addActivity]);

  // WebSocket connection for real-time updates
  const { isConnected, connectionCount } = useWorkflowWebSocket(handleWebSocketUpdate);
  
  // Auto-refresh interval as fallback for unreliable webhooks
  const [lastAutoRefresh, setLastAutoRefresh] = useState(null);
  // Default OFF on production: auto-loading the entire workflow on every
  // home-page visit was making the rest of the portal feel slow. The user
  // explicitly opts in by clicking the "Load workflow" button (or hitting
  // Refresh once loaded). Persisted in sessionStorage so a "load once per
  // session" pattern keeps the page snappy without re-fetching on every nav.
  const [hasLoaded, setHasLoaded] = useState(() => {
    try { return sessionStorage.getItem("workflow_loaded_once") === "true"; }
    catch { return false; }
  });
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds — only runs when WS is disconnected

  // Load workflow data — Phase A: ONE call via /workflow/snapshot
  // instead of 13 sequential per-group requests
  const loadWorkflowData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      const snap = await getWorkflowSnapshot(5000);
      const grps = snap?.groups || [];
      setGroups(grps);

      // Expand all groups on first load only
      if (Object.keys(expandedGroups).length === 0) {
        const expanded = {};
        grps.forEach((g) => { expanded[g.id] = true; });
        setExpandedGroups(expanded);
      }

      setItems(snap?.items || {});
      setStats({
        totalItems: snap?.stats?.total_items || 0,
        totalValue: snap?.stats?.total_value || 0,
      });
      setLastDataUpdate(new Date());
      if (silent) setLastAutoRefresh(new Date());
      // Mark loaded for this session so navigating away + back doesn't
      // re-trigger the empty-state gate (data still goes stale via WS push
      // + polling fallback).
      setHasLoaded(true);
      try { sessionStorage.setItem("workflow_loaded_once", "true"); } catch {}
    } catch (error) {
      console.error("Failed to load workflow data:", error);
      if (!silent) toast.error("Failed to load workflow data");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [expandedGroups]);

  // Polling fallback — only kicks in once the user has explicitly loaded
  // workflow data at least once this session AND the WebSocket is offline.
  useEffect(() => {
    if (!hasLoaded) return;          // don't poll before initial load
    if (!autoRefreshEnabled) return;
    if (isConnected) return;          // WS is the realtime source; no polling needed
    const interval = setInterval(() => {
      console.log("🔄 Auto-refresh (WS disconnected, polling fallback)");
      loadWorkflowData(true);
    }, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [hasLoaded, autoRefreshEnabled, isConnected, loadWorkflowData]);

  // On mount: only load the small "available statuses" lookup. The heavy
  // workflow snapshot is loaded lazily on user action (or auto-resumes
  // within the same session via sessionStorage flag).
  useEffect(() => {
    getWorkflowStatuses().then(data => {
      setAvailableStatuses(data.statuses || []);
    }).catch(err => console.error("Failed to load statuses:", err));
    // If this session already loaded the workflow once, restore it
    // automatically (it should be cheap to fetch a second time and the user
    // expects it ready when re-visiting).
    if (hasLoaded) loadWorkflowData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync from Monday.com
  const handleSync = async () => {
    try {
      setSyncing(true);
      const result = await syncWorkflowItems("2025-04-01");
      toast.success(`Synced ${result.total} items (${result.created} new, ${result.updated} updated)`);
      loadWorkflowData();
    } catch (error) {
      console.error("Sync failed:", error);
      toast.error("Failed to sync from Monday.com");
    } finally {
      setSyncing(false);
    }
  };

  // Change item status (pushes to Monday.com)
  const handleStatusChange = async (newStatus) => {
    if (!selectedItem || !newStatus) return;
    
    try {
      setUpdatingStatus(true);
      await updateWorkflowItemStatus(selectedItem.monday_item_id, newStatus);
      
      // Update local state
      setSelectedItem(prev => ({ ...prev, status: newStatus }));
      
      // Update in items list
      setItems(prevItems => {
        const newItems = { ...prevItems };
        const groupId = selectedItem.group_id;
        if (newItems[groupId]) {
          const idx = newItems[groupId].findIndex(i => i.monday_item_id === selectedItem.monday_item_id);
          if (idx >= 0) {
            newItems[groupId][idx] = { ...newItems[groupId][idx], status: newStatus };
          }
        }
        return newItems;
      });
      
      toast.success(`Status updated to "${newStatus}"`);
      setLastDataUpdate(new Date());
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Inline status change (from main view, like Monday.com)
  const handleInlineStatusChange = async (item, newStatus, groupId) => {
    if (!item || !newStatus || item.status === newStatus) return;
    
    // Create a countdown toast ID so we can update it
    const toastId = `status-${item.monday_item_id}`;
    
    try {
      setInlineUpdatingItemId(item.monday_item_id);
      
      // Show initial toast with countdown - 3
      toast.loading(
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{item.sf_number || item.name}</span>
            <span>→</span>
            <span 
              className="px-2 py-0.5 rounded text-white text-xs font-medium"
              style={{ backgroundColor: getStatusColor(newStatus) }}
            >
              {newStatus}
            </span>
          </div>
          <div className="flex items-center gap-1 text-blue-500 font-mono font-bold">
            <span className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-sm">3</span>
          </div>
        </div>,
        { id: toastId, duration: Infinity }
      );
      
      // Make the API call
      await updateWorkflowItemStatus(item.monday_item_id, newStatus);
      
      // Update local status immediately
      setItems(prevItems => {
        const newItems = { ...prevItems };
        if (newItems[groupId]) {
          const idx = newItems[groupId].findIndex(i => i.monday_item_id === item.monday_item_id);
          if (idx >= 0) {
            newItems[groupId][idx] = { ...newItems[groupId][idx], status: newStatus };
          }
        }
        return newItems;
      });
      
      // Countdown: 2
      setTimeout(() => {
        toast.loading(
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{item.sf_number || item.name}</span>
              <span>→</span>
              <span 
                className="px-2 py-0.5 rounded text-white text-xs font-medium"
                style={{ backgroundColor: getStatusColor(newStatus) }}
              >
                {newStatus}
              </span>
            </div>
            <div className="flex items-center gap-1 text-amber-500 font-mono font-bold">
              <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-sm">2</span>
            </div>
          </div>,
          { id: toastId, duration: Infinity }
        );
      }, 1000);
      
      // Countdown: 1
      setTimeout(() => {
        toast.loading(
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{item.sf_number || item.name}</span>
              <span>→</span>
              <span 
                className="px-2 py-0.5 rounded text-white text-xs font-medium"
                style={{ backgroundColor: getStatusColor(newStatus) }}
              >
                {newStatus}
              </span>
            </div>
            <div className="flex items-center gap-1 text-orange-500 font-mono font-bold">
              <span className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center text-sm">1</span>
            </div>
          </div>,
          { id: toastId, duration: Infinity }
        );
      }, 2000);
      
      // Final success message
      setTimeout(() => {
        toast.success(
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{item.sf_number || item.name}</span>
              <span>→</span>
              <span 
                className="px-2 py-0.5 rounded text-white text-xs font-medium"
                style={{ backgroundColor: getStatusColor(newStatus) }}
              >
                {newStatus}
              </span>
            </div>
            <span className="text-emerald-600 font-medium">✓ Synced!</span>
          </div>,
          { id: toastId, duration: 3000 }
        );
        setInlineUpdatingItemId(null);
      }, 3000);
      
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status", { id: toastId });
      setInlineUpdatingItemId(null);
    }
  };

  // Move item to different group (pushes to Monday.com)
  const handleMoveItem = async (targetGroupId) => {
    if (!selectedItem || !targetGroupId || targetGroupId === selectedItem.group_id) return;
    
    const targetGroup = groups.find(g => g.id === targetGroupId);
    
    try {
      setMovingItem(true);
      await moveWorkflowItem(selectedItem.monday_item_id, targetGroupId);
      
      // Update local state - remove from old group, add to new
      setItems(prevItems => {
        const newItems = { ...prevItems };
        
        // Remove from current group
        if (newItems[selectedItem.group_id]) {
          newItems[selectedItem.group_id] = newItems[selectedItem.group_id].filter(
            i => i.monday_item_id !== selectedItem.monday_item_id
          );
        }
        
        // Add to new group
        if (!newItems[targetGroupId]) {
          newItems[targetGroupId] = [];
        }
        const movedItem = { 
          ...selectedItem, 
          group_id: targetGroupId, 
          group_name: targetGroup?.name || targetGroupId 
        };
        newItems[targetGroupId] = [movedItem, ...newItems[targetGroupId]];
        
        return newItems;
      });
      
      // Update selected item
      setSelectedItem(prev => ({ 
        ...prev, 
        group_id: targetGroupId, 
        group_name: targetGroup?.name || targetGroupId 
      }));
      
      toast.success(`Moved to "${targetGroup?.name || targetGroupId}"`);
    } catch (error) {
      console.error("Failed to move item:", error);
      toast.error("Failed to move item");
    } finally {
      setMovingItem(false);
    }
  };

  // Post update/comment (pushes to Monday.com)
  const handlePostUpdate = async () => {
    if (!selectedItem || !newUpdateText.trim()) return;
    
    try {
      setPostingUpdate(true);
      await postWorkflowItemUpdate(selectedItem.monday_item_id, newUpdateText);
      
      // Add to local updates list
      const newUpdate = {
        id: `local-${Date.now()}`,
        body: newUpdateText,
        text_body: newUpdateText,
        created_at: new Date().toISOString(),
        creator: { name: "You (Portal)", email: "" }
      };
      setItemUpdates(prev => [newUpdate, ...prev]);
      setNewUpdateText("");
      
      toast.success("Update posted to Monday.com");
    } catch (error) {
      console.error("Failed to post update:", error);
      toast.error("Failed to post update");
    } finally {
      setPostingUpdate(false);
    }
  };

  // Open item modal and load updates
  const openItemModal = async (item) => {
    setSelectedItem(item);
    setShowItemModal(true);
    setActiveTab("details");
    setItemUpdates([]);
    setItemColumns({});
    // Reset underwriting state
    setFilteringResult(null);
    setAssessmentResult(null);
    setDigitalFootprintResult(null);
    setDirectorNetworkResult(null);
    
    // Load updates from Monday.com
    if (item.monday_item_id) {
      try {
        setUpdatesLoading(true);
        const data = await getMondayItemUpdates(item.monday_item_id, 50);
        setItemUpdates(data.updates || []);
        setItemColumns(data.columns || {});
        
        // Try to load existing underwriting data
        try {
          const uwStatus = await getUnderwritingStatus(item.id || item.monday_item_id);
          if (uwStatus) {
            setFilteringResult(uwStatus.filtering);
            setAssessmentResult(uwStatus.assessment);
          }
        } catch (uwError) {
          // No underwriting data yet - that's fine
          console.log("No underwriting data found for item");
        }
      } catch (error) {
        console.error("Failed to load updates:", error);
      } finally {
        setUpdatesLoading(false);
      }
    }
  };

  // Run underwriting filtering checks
  const handleRunFiltering = async () => {
    if (!selectedItem) return;
    
    try {
      setUnderwritingLoading(true);
      
      // Extract company name from item (usually formatted as "SF-XXXX - Company Name")
      const companyName = selectedItem.name?.split(' - ').slice(1).join(' - ') || selectedItem.name || "";
      
      // Try to get director name from columns or item data
      const directorName = itemColumns['Director Name'] || itemColumns['director_name'] || "";
      const companyNumber = itemColumns['Company Number'] || itemColumns['company_number'] || "";
      
      const result = await runUnderwritingFiltering({
        company_name: companyName,
        company_number: companyNumber,
        director_name: directorName,
        application_id: selectedItem.id || selectedItem.monday_item_id
      });
      
      setFilteringResult(result);
      
      if (result.filtering_passed) {
        toast.success("Filtering checks passed! Ready for full assessment.");
      } else {
        toast.warning("Filtering checks flagged issues. Review decline reasons.");
      }
    } catch (error) {
      console.error("Filtering error:", error);
      toast.error("Failed to run filtering checks");
    } finally {
      setUnderwritingLoading(false);
    }
  };

  // Run full underwriting assessment
  const handleRunFullAssessment = async () => {
    if (!selectedItem) return;
    
    try {
      setUnderwritingLoading(true);
      
      const companyName = selectedItem.name?.split(' - ').slice(1).join(' - ') || selectedItem.name || "";
      const directorName = itemColumns['Director Name'] || itemColumns['director_name'] || "";
      const companyNumber = itemColumns['Company Number'] || itemColumns['company_number'] || "";
      const loanAmount = selectedItem.loan_amount || 10000;
      
      // Estimate monthly payment (simple approximation)
      const duration = selectedItem.duration || 12;
      const factor = selectedItem.factor || 1.4;
      const monthlyPayment = (loanAmount * factor) / duration;
      
      const result = await runFullUnderwritingAssessment({
        company_name: companyName,
        company_number: companyNumber,
        director_name: directorName,
        loan_amount: loanAmount,
        monthly_payment: monthlyPayment,
        application_id: selectedItem.id || selectedItem.monday_item_id
      });
      
      setAssessmentResult(result);
      
      if (result.decision === "APPROVED") {
        toast.success(`Assessment complete: ${result.decision} (Risk Factor: ${result.overall_risk_factor})`);
      } else if (result.decision === "CONDITIONAL") {
        toast.warning(`Assessment complete: ${result.decision} - Review conditions`);
      } else {
        toast.error(`Assessment complete: ${result.decision}`);
      }
    } catch (error) {
      console.error("Assessment error:", error);
      toast.error("Failed to run full assessment");
    } finally {
      setUnderwritingLoading(false);
    }
  };

  // Check digital footprint only
  const handleCheckDigitalFootprint = async () => {
    if (!selectedItem) return;
    
    try {
      setUnderwritingLoading(true);
      const companyName = selectedItem.name?.split(' - ').slice(1).join(' - ') || selectedItem.name || "";
      const result = await checkDigitalFootprint(companyName);
      setDigitalFootprintResult(result);
      
      if (!result.error) {
        toast.success(`Digital footprint found: Reputation score ${result.reputation_score}/5`);
      } else {
        toast.warning("Could not fetch digital footprint data");
      }
    } catch (error) {
      console.error("Digital footprint error:", error);
      toast.error("Failed to check digital footprint");
    } finally {
      setUnderwritingLoading(false);
    }
  };

  // Check director network only
  const handleCheckDirectorNetwork = async () => {
    const directorName = itemColumns['Director Name'] || itemColumns['director_name'] || "";
    if (!directorName) {
      toast.warning("No director name available for network check");
      return;
    }
    
    try {
      setUnderwritingLoading(true);
      const result = await checkDirectorNetwork(directorName);
      setDirectorNetworkResult(result);
      
      if (!result.error) {
        const insolvencyCount = result.insolvency_flags?.length || 0;
        if (insolvencyCount > 0) {
          toast.warning(`Director has ${insolvencyCount} previous insolvencies`);
        } else {
          toast.success("Director network check complete - no insolvencies found");
        }
      } else {
        toast.warning("Could not fetch director network data");
      }
    } catch (error) {
      console.error("Director network error:", error);
      toast.error("Failed to check director network");
    } finally {
      setUnderwritingLoading(false);
    }
  };

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Filter items by search
  // Get AI opinion badge
  const getAIOpinionBadge = (opinion) => {
    const config = AI_OPINION_COLORS[opinion] || { bg: "bg-gray-100", text: "text-gray-700", label: opinion || "N/A" };
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // First-visit empty state — user hasn't loaded workflow data this session.
  // We don't auto-fetch because the snapshot is heavy and used to slow down
  // the rest of the portal in production. Show this BEFORE the loading check
  // so the spinner only appears once the user has actually clicked Load.
  if (!hasLoaded && !loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="workflow-not-loaded">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-50 flex items-center justify-center">
            <Wifi className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Applications Workflow</h2>
          <p className="text-sm text-muted-foreground mb-6">
            We don't load the full workflow automatically anymore — it was making the rest of the portal feel slow. Click below when you need it. Live updates will start once loaded and stay live for the rest of your session.
          </p>
          <Button
            onClick={() => loadWorkflowData()}
            size="lg"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="load-workflow-btn"
          >
            Load workflow data
          </Button>
          <p className="text-xs text-muted-foreground mt-4">
            Once loaded, refreshes happen automatically via WebSocket.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-3" />
          <p className="text-muted-foreground">Loading workflow data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            Applications Workflow
            {/* WebSocket Status Indicator */}
            <span 
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                isConnected 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}
              title={isConnected ? `Live - ${connectionCount} connected` : 'Disconnected - Reconnecting...'}
            >
              {isConnected ? (
                <>
                  <Wifi className="w-3 h-3" />
                  Live
                </>
              ) : (
                <>
                  <WifiOff className="w-3 h-3" />
                  Offline
                </>
              )}
            </span>
            {/* Last-update indicator — shows how fresh the displayed data is.
                Updates from WS pushes, snapshot loads, and polling fallback. */}
            {lastDataUpdate && (
              <span
                className="text-[10px] text-muted-foreground ml-1"
                title={`Last update: ${lastDataUpdate.toLocaleTimeString("en-GB")}${isConnected ? " (live)" : " (polling)"}`}
                data-testid="last-data-update"
              >
                · updated {lastDataUpdate.toLocaleTimeString("en-GB")}
              </span>
            )}
          </h1>
          <p className="text-muted-foreground text-sm">
            Real-time view of all applications in the funding pipeline
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Stats */}
          <div className="hidden md:flex items-center gap-4 mr-4 text-sm">
            <div className="flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="font-semibold">{stats.totalItems}</span>
              <span className="text-muted-foreground">items</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Banknote className="w-4 h-4 text-muted-foreground" />
              <span className="font-semibold">£{stats.totalValue.toLocaleString()}</span>
              <span className="text-muted-foreground">in pipeline</span>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search applications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Activity Bell */}
          <Popover open={showActivityPanel} onOpenChange={(open) => {
            setShowActivityPanel(open);
            if (open) setUnreadActivityCount(0);
          }}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="relative"
                data-testid="activity-bell"
              >
                <Bell className="w-4 h-4" />
                {unreadActivityCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {unreadActivityCount > 99 ? '99+' : unreadActivityCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-96 p-0" align="end">
              <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold">Live Activity</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setActivityFeed([])}
                >
                  Clear All
                </Button>
              </div>
              <ScrollArea className="h-80">
                {activityFeed.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                    <Bell className="w-8 h-8 mb-2 opacity-50" />
                    <p className="text-sm">No recent activity</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {activityFeed.map((activity) => (
                      <div key={activity.id} className="px-4 py-3 hover:bg-slate-50 transition-colors">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <ArrowRight className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {activity.sfNumber || activity.itemName}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {activity.fromGroup && activity.toGroup && activity.fromGroup !== activity.toGroup ? (
                                <>
                                  Moved from <span className="font-medium">{activity.fromGroup}</span> to <span className="font-medium text-emerald-600">{activity.toGroup}</span>
                                </>
                              ) : (
                                <>
                                  Updated in <span className="font-medium text-emerald-600">{activity.toGroup}</span>
                                </>
                              )}
                            </p>
                            {activity.status && (
                              <p className="text-xs text-muted-foreground">
                                Status: <span className="font-medium">{activity.status}</span>
                              </p>
                            )}
                            <p className="text-[10px] text-muted-foreground mt-1">
                              {new Date(activity.timestamp).toLocaleTimeString("en-GB")}
                              {activity.source === 'monday' && (
                                <span className="ml-2 text-blue-500">via Monday.com</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              <div className="px-4 py-2 border-t bg-slate-50 text-xs text-muted-foreground">
                <a href="/audit-trail" className="text-blue-500 hover:underline">
                  View full audit log →
                </a>
              </div>
            </PopoverContent>
          </Popover>
          
          {/* Sync Button */}
          <Button
            onClick={handleSync}
            disabled={syncing}
            variant="outline"
            className="gap-2"
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Sync Monday
          </Button>
        </div>
      </div>

      {/* Workflow Groups */}
      <div className="space-y-3">
        {groups.filter(group => !HIDDEN_GROUPS.includes(group.id)).map((group) => {
          const groupItems = items[group.id] || [];
          const filteredItems = getFilteredSortedItems(group.id, groupItems);
          const isExpanded = expandedGroups[group.id];
          const groupColor = GROUP_COLORS[group.id] || "#6b7280";
          // Prefer true totals from the backend snapshot (group.count / group.total_value)
          // — these stay accurate even when a group is item-capped on the frontend.
          const trueGroupCount = typeof group.count === "number" ? group.count : groupItems.length;
          const totalAmount = typeof group.total_value === "number"
            ? group.total_value
            : groupItems.reduce((sum, item) => sum + (item.loan_amount || 0), 0);
          const isCapped = groupItems.length < trueGroupCount;
          
          return (
            <Card key={group.id} className="overflow-hidden">
              {/* Group Header */}
              <div 
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleGroup(group.id)}
                style={{ borderLeft: `4px solid ${groupColor}` }}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  )}
                  <h2 className="font-semibold">{group.name}</h2>
                  <Badge variant="secondary" className="font-mono" data-testid={`group-count-${group.id}`}>
                    {searchQuery && filteredItems.length !== groupItems.length ? (
                      <>
                        {filteredItems.length}
                        <span className="text-muted-foreground ml-1">/ {trueGroupCount}</span>
                      </>
                    ) : (
                      trueGroupCount
                    )}
                  </Badge>
                  {isCapped && !searchQuery && (
                    <Badge
                      variant="outline"
                      className="font-mono text-[10px] text-amber-700 border-amber-300 bg-amber-50"
                      data-testid={`group-capped-${group.id}`}
                      title={`Showing ${groupItems.length} of ${trueGroupCount} items (capped for performance)`}
                    >
                      Showing {groupItems.length.toLocaleString()} of {trueGroupCount.toLocaleString()}
                    </Badge>
                  )}
                  
                  {/* Filter Toggle Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFilters(prev => ({ ...prev, [group.id]: !prev[group.id] }));
                    }}
                  >
                    <SlidersHorizontal className="w-3 h-3 mr-1" />
                    Filter
                    {Object.values(groupFilters[group.id] || {}).some(v => v) && (
                      <span className="ml-1 w-4 h-4 bg-blue-500 text-white rounded-full text-[10px] flex items-center justify-center">
                        {Object.values(groupFilters[group.id] || {}).filter(v => v).length}
                      </span>
                    )}
                  </Button>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>£{totalAmount.toLocaleString()}</span>
                </div>
              </div>
              
              {/* Group Items */}
              {isExpanded && (
                <div className="border-t overflow-x-auto">
                  {filteredItems.length === 0 && !Object.values(groupFilters[group.id] || {}).some(v => v) ? (
                    <div className="px-4 py-6 text-center text-muted-foreground text-xs">
                      {searchQuery ? "No matching items" : "No items in this group"}
                    </div>
                  ) : (
                    <div className="min-w-[1600px]">
                      {/* Column Headers - Sortable */}
                      <div className="flex items-center px-2 py-1.5 bg-slate-100 border-b text-[9px] font-semibold text-muted-foreground uppercase tracking-wide">
                        <button 
                          className="w-[280px] flex-shrink-0 px-1 flex items-center gap-1 hover:text-foreground transition-colors"
                          onClick={() => toggleGroupSort(group.id, 'name')}
                        >
                          Funding Application
                          {groupSort[group.id]?.column === 'name' && (
                            groupSort[group.id]?.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          )}
                          {groupSort[group.id]?.column !== 'name' && <ArrowUpDown className="w-3 h-3 opacity-30" />}
                        </button>
                        <button 
                          className="w-[70px] flex-shrink-0 px-1 flex items-center gap-1 hover:text-foreground transition-colors"
                          onClick={() => toggleGroupSort(group.id, 'date_created')}
                        >
                          Date
                          {groupSort[group.id]?.column === 'date_created' && (
                            groupSort[group.id]?.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          )}
                        </button>
                        <button 
                          className="w-[90px] flex-shrink-0 px-1 text-center flex items-center justify-center gap-1 hover:text-foreground transition-colors"
                          onClick={() => toggleGroupSort(group.id, 'status')}
                        >
                          Status
                          {groupSort[group.id]?.column === 'status' && (
                            groupSort[group.id]?.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          )}
                        </button>
                        <button 
                          className="w-[55px] flex-shrink-0 px-1 text-center flex items-center justify-center gap-1 hover:text-foreground transition-colors"
                          onClick={() => toggleGroupSort(group.id, 'ai_opinion')}
                        >
                          AI
                          {groupSort[group.id]?.column === 'ai_opinion' && (
                            groupSort[group.id]?.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          )}
                        </button>
                        <div className="w-[55px] flex-shrink-0 px-1 text-center">Type</div>
                        <button 
                          className="w-[110px] flex-shrink-0 px-1 flex items-center gap-1 hover:text-foreground transition-colors"
                          onClick={() => toggleGroupSort(group.id, 'broker_name')}
                        >
                          Broker Name
                          {groupSort[group.id]?.column === 'broker_name' && (
                            groupSort[group.id]?.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          )}
                        </button>
                        <div className="w-[45px] flex-shrink-0 px-1 text-center">Comm%</div>
                        <div className="w-[150px] flex-shrink-0 px-1">Broker Email</div>
                        <div className="w-[100px] flex-shrink-0 px-1">Contact</div>
                        <div className="w-[150px] flex-shrink-0 px-1">Customer Email</div>
                        <div className="w-[80px] flex-shrink-0 px-1">Underwriter</div>
                        <button 
                          className="w-[70px] flex-shrink-0 px-1 text-right flex items-center justify-end gap-1 hover:text-foreground transition-colors"
                          onClick={() => toggleGroupSort(group.id, 'outstanding_balance')}
                        >
                          OB
                          {groupSort[group.id]?.column === 'outstanding_balance' && (
                            groupSort[group.id]?.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          )}
                        </button>
                        <button 
                          className="w-[80px] flex-shrink-0 px-1 text-right flex items-center justify-end gap-1 hover:text-foreground transition-colors"
                          onClick={() => toggleGroupSort(group.id, 'loan_amount')}
                        >
                          Offer Amt
                          {groupSort[group.id]?.column === 'loan_amount' && (
                            groupSort[group.id]?.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                          )}
                        </button>
                        <div className="w-6 flex-shrink-0"></div>
                      </div>
                      
                      {/* Filter Row */}
                      {showFilters[group.id] && (
                        <div className="flex items-center px-2 py-1 bg-blue-50/50 border-b gap-0">
                          <div className="w-[280px] flex-shrink-0 px-1">
                            <input
                              type="text"
                              placeholder="Filter name..."
                              className="w-full h-6 px-2 text-[10px] border rounded bg-white"
                              value={groupFilters[group.id]?.name || ''}
                              onChange={(e) => updateGroupFilter(group.id, 'name', e.target.value)}
                            />
                          </div>
                          <div className="w-[70px] flex-shrink-0 px-1"></div>
                          <div className="w-[90px] flex-shrink-0 px-1">
                            <input
                              type="text"
                              placeholder="Status..."
                              className="w-full h-6 px-2 text-[10px] border rounded bg-white"
                              value={groupFilters[group.id]?.status || ''}
                              onChange={(e) => updateGroupFilter(group.id, 'status', e.target.value)}
                            />
                          </div>
                          <div className="w-[55px] flex-shrink-0 px-1">
                            <input
                              type="text"
                              placeholder="AI..."
                              className="w-full h-6 px-1 text-[10px] border rounded bg-white"
                              value={groupFilters[group.id]?.ai_opinion || ''}
                              onChange={(e) => updateGroupFilter(group.id, 'ai_opinion', e.target.value)}
                            />
                          </div>
                          <div className="w-[55px] flex-shrink-0 px-1"></div>
                          <div className="w-[110px] flex-shrink-0 px-1">
                            <input
                              type="text"
                              placeholder="Broker..."
                              className="w-full h-6 px-2 text-[10px] border rounded bg-white"
                              value={groupFilters[group.id]?.broker_name || ''}
                              onChange={(e) => updateGroupFilter(group.id, 'broker_name', e.target.value)}
                            />
                          </div>
                          <div className="w-[45px] flex-shrink-0 px-1"></div>
                          <div className="w-[150px] flex-shrink-0 px-1"></div>
                          <div className="w-[100px] flex-shrink-0 px-1">
                            <input
                              type="text"
                              placeholder="Contact..."
                              className="w-full h-6 px-2 text-[10px] border rounded bg-white"
                              value={groupFilters[group.id]?.contact_name || ''}
                              onChange={(e) => updateGroupFilter(group.id, 'contact_name', e.target.value)}
                            />
                          </div>
                          <div className="w-[150px] flex-shrink-0 px-1"></div>
                          <div className="w-[80px] flex-shrink-0 px-1">
                            <input
                              type="text"
                              placeholder="UW..."
                              className="w-full h-6 px-1 text-[10px] border rounded bg-white"
                              value={groupFilters[group.id]?.account_manager || ''}
                              onChange={(e) => updateGroupFilter(group.id, 'account_manager', e.target.value)}
                            />
                          </div>
                          <div className="w-[70px] flex-shrink-0 px-1"></div>
                          <div className="w-[80px] flex-shrink-0 px-1"></div>
                          <div className="w-6 flex-shrink-0">
                            <button
                              className="p-0.5 hover:bg-red-100 rounded text-red-500"
                              onClick={() => clearGroupFilters(group.id)}
                              title="Clear filters"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Items List */}
                      <div className="divide-y max-h-[280px] overflow-y-scroll" style={{ scrollbarWidth: 'thin' }}>
                        {filteredItems.length === 0 ? (
                          <div className="px-4 py-6 text-center text-muted-foreground text-xs">
                            No items match your filters
                            <button 
                              className="ml-2 text-blue-500 hover:underline"
                              onClick={() => clearGroupFilters(group.id)}
                            >
                              Clear filters
                            </button>
                          </div>
                        ) : filteredItems.map((item) => (
                          <div 
                            key={item.monday_item_id}
                            className="flex items-center px-2 py-1 hover:bg-blue-50/50 transition-colors group text-[11px]"
                          >
                            {/* Funding Application - Full Name (SF-XXXX - Company Name) */}
                            <div 
                              className="w-[280px] flex-shrink-0 px-1 font-medium truncate cursor-pointer hover:text-blue-600"
                              onClick={() => openItemModal(item)}
                              title={item.name}
                            >
                              {item.name || "—"}
                            </div>
                            
                            {/* Date of App */}
                            <div className="w-[70px] flex-shrink-0 px-1 text-muted-foreground truncate">
                              {item.date_created ? new Date(item.date_created).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : "—"}
                            </div>
                            
                            {/* Status - Clickable with categorized dropdown */}
                            <div className="w-[90px] flex-shrink-0 px-1" onClick={(e) => e.stopPropagation()}>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    className="w-full px-1.5 py-0.5 rounded text-[10px] font-medium text-white truncate hover:opacity-90 transition-opacity"
                                    style={{ backgroundColor: getStatusColor(item.status) }}
                                    disabled={inlineUpdatingItemId === item.monday_item_id}
                                  >
                                    {inlineUpdatingItemId === item.monday_item_id ? (
                                      <Loader2 className="w-3 h-3 animate-spin mx-auto" />
                                    ) : (
                                      item.status || "—"
                                    )}
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[500px] p-3" align="start" side="bottom">
                                  <div className="grid grid-cols-4 gap-4">
                                    {Object.entries(STATUS_CATEGORIES).map(([category, statuses]) => (
                                      <div key={category}>
                                        <h4 className="text-[10px] font-semibold text-muted-foreground mb-1.5 uppercase">{category}</h4>
                                        <div className="space-y-1">
                                          {statuses.map((status) => (
                                            <button
                                              key={status}
                                              onClick={() => handleInlineStatusChange(item, status, group.id)}
                                              className="w-full px-2 py-1 rounded text-[10px] font-medium text-white text-left truncate hover:opacity-90 transition-opacity flex items-center justify-between"
                                              style={{ backgroundColor: getStatusColor(status) }}
                                            >
                                              <span className="truncate">{status}</span>
                                              {item.status === status && <Check className="w-3 h-3 flex-shrink-0" />}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                            
                            {/* AI Opinion */}
                            <div className="w-[55px] flex-shrink-0 px-1 text-center">
                              {getAIOpinionBadge(item.ai_opinion)}
                            </div>
                            
                            {/* Broker/Direct Type */}
                            <div className="w-[55px] flex-shrink-0 px-1 text-center">
                              <span className={`text-[9px] px-1.5 py-0.5 rounded ${item.broker_name ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'}`}>
                                {item.broker_name ? 'Broker' : 'Direct'}
                              </span>
                            </div>
                            
                            {/* Broker Name */}
                            <div className="w-[110px] flex-shrink-0 px-1 truncate text-muted-foreground">
                              {item.broker_name || "—"}
                            </div>
                            
                            {/* Commission % */}
                            <div className="w-[45px] flex-shrink-0 px-1 text-center text-muted-foreground">
                              {item.commission_rate ? `${item.commission_rate}%` : "—"}
                            </div>
                            
                            {/* Broker/Contact Email */}
                            <div className="w-[150px] flex-shrink-0 px-1 truncate text-muted-foreground">
                              {item.broker_email || item.contact_email || "—"}
                            </div>
                            
                            {/* Contact Name */}
                            <div className="w-[100px] flex-shrink-0 px-1 truncate">
                              {item.contact_name || "—"}
                            </div>
                            
                            {/* Customer Email */}
                            <div className="w-[150px] flex-shrink-0 px-1 truncate text-muted-foreground">
                              {item.customer_email || item.contact_email || "—"}
                            </div>
                            
                            {/* Underwriter (was Account Manager) */}
                            <div className="w-[80px] flex-shrink-0 px-1 truncate">
                              {item.account_manager || "—"}
                            </div>
                            
                            {/* OB (Outstanding Balance) */}
                            <div className="w-[70px] flex-shrink-0 px-1 text-right font-medium">
                              {item.outstanding_balance ? `£${item.outstanding_balance.toLocaleString()}` : "—"}
                            </div>
                            
                            {/* Offer Amount */}
                            <div className="w-[80px] flex-shrink-0 px-1 text-right font-semibold text-emerald-600">
                              £{(item.loan_amount || 0).toLocaleString()}
                            </div>
                            
                            {/* Eye icon */}
                            <div className="w-6 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button 
                                onClick={() => openItemModal(item)}
                                className="p-0.5 hover:bg-slate-200 rounded"
                              >
                                <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {groups.length === 0 && (
        <Card className="bg-slate-50/50">
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Workflow Data</h3>
            <p className="text-muted-foreground mb-4">
              Click &quot;Sync Monday&quot; to import applications from Monday.com
            </p>
            <Button onClick={handleSync} disabled={syncing}>
              {syncing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sync from Monday.com
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Item Detail Modal */}
      <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
              {selectedItem?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedItem?.sf_number} • {selectedItem?.group_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedItem && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
              <TabsList className="w-full justify-start flex-shrink-0">
                <TabsTrigger value="details" className="gap-1.5">
                  <FileText className="w-4 h-4" />
                  Details
                </TabsTrigger>
                <TabsTrigger value="underwriting" className="gap-1.5" data-testid="underwriting-tab">
                  <ShieldCheck className="w-4 h-4" />
                  Underwriting
                  {(filteringResult || assessmentResult) && (
                    <span className={`ml-1 w-2 h-2 rounded-full ${
                      assessmentResult?.decision === 'APPROVED' ? 'bg-green-500' :
                      assessmentResult?.decision === 'DECLINED' ? 'bg-red-500' :
                      filteringResult?.filtering_passed ? 'bg-yellow-500' : 'bg-slate-400'
                    }`} />
                  )}
                </TabsTrigger>
                <TabsTrigger value="actions" className="gap-1.5">
                  <ArrowRight className="w-4 h-4" />
                  Actions
                </TabsTrigger>
                <TabsTrigger value="updates" className="gap-1.5">
                  <MessageCircle className="w-4 h-4" />
                  Updates
                  {itemUpdates.length > 0 && (
                    <Badge variant="secondary" className="ml-1 text-xs">
                      {itemUpdates.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              
              <div className="flex-1 mt-4 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                <TabsContent value="details" className="m-0 space-y-4 pr-2">
                  {/* Key Info Card */}
                  <Card className="bg-emerald-50/50 border-emerald-200">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">SF Number</p>
                          <p className="font-mono font-semibold">{selectedItem.sf_number || "—"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Loan Amount</p>
                          <p className="font-bold text-emerald-700 text-lg">
                            £{(selectedItem.loan_amount || 0).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Current Status</p>
                          <Badge variant="outline">{selectedItem.status || "—"}</Badge>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">AI Opinion</p>
                          {getAIOpinionBadge(selectedItem.ai_opinion)}
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Broker</p>
                          <p className="font-semibold">{selectedItem.broker_name || "Direct"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Commission</p>
                          <p className="font-semibold">{selectedItem.commission || "—"}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Duration</p>
                          <p className="font-semibold">{selectedItem.duration || "—"} months</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Factor</p>
                          <p className="font-semibold">{selectedItem.factor || "—"}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Monday.com Fields */}
                  {Object.keys(itemColumns).length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Database className="w-4 h-4" />
                          Monday.com Fields
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                          {Object.entries(itemColumns).slice(0, 15).map(([key, value]) => (
                            <div key={key} className="bg-slate-50 rounded p-2 border">
                              <p className="text-muted-foreground truncate">{key}</p>
                              <p className="font-medium truncate" title={value}>{value || "—"}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Contact & Account Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Contact Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-2">
                        <div>
                          <span className="text-muted-foreground">Name: </span>
                          <span className="font-medium">{selectedItem.contact_name || "—"}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Email: </span>
                          <span className="font-medium">{selectedItem.contact_email || "—"}</span>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Account Manager
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm">
                        <span className="font-medium">{selectedItem.account_manager || "Not assigned"}</span>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Underwriting Tab - Enhanced assessment funnel */}
                <TabsContent value="underwriting" className="m-0" data-testid="underwriting-content">
                  <UnderwritingPanel 
                    item={selectedItem} 
                    itemColumns={itemColumns}
                  />
                </TabsContent>
                
                {/* Actions Tab - Control panel for workflow actions */}
                <TabsContent value="actions" className="m-0 space-y-4 pr-2">
                  {/* Status Change */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Change Status</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <Select 
                            value={selectedItem.status || ""} 
                            onValueChange={handleStatusChange}
                            disabled={updatingStatus}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableStatuses.map(status => (
                                <SelectItem key={status.id} value={status.label}>
                                  {status.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {updatingStatus && <Loader2 className="w-4 h-4 animate-spin" />}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Changing status will trigger Monday.com automations
                      </p>
                    </CardContent>
                  </Card>

                  {/* Move to Group */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Move to Stage</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <Select 
                            value={selectedItem.group_id} 
                            onValueChange={handleMoveItem}
                            disabled={movingItem}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select stage" />
                            </SelectTrigger>
                            <SelectContent>
                              {groups.filter(group => !HIDDEN_GROUPS.includes(group.id)).map(group => (
                                <SelectItem key={group.id} value={group.id}>
                                  <div className="flex items-center gap-2">
                                    <div 
                                      className="w-2 h-2 rounded-full" 
                                      style={{ backgroundColor: GROUP_COLORS[group.id] || "#6b7280" }}
                                    />
                                    {group.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {movingItem && <Loader2 className="w-4 h-4 animate-spin" />}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Current stage: <span className="font-medium">{selectedItem.group_name}</span>
                      </p>
                    </CardContent>
                  </Card>

                  {/* Post Update */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Add Update / Note</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Textarea
                        placeholder="Type your update or note here..."
                        value={newUpdateText}
                        onChange={(e) => setNewUpdateText(e.target.value)}
                        rows={3}
                        disabled={postingUpdate}
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                          This will be posted to Monday.com
                        </p>
                        <Button 
                          size="sm" 
                          onClick={handlePostUpdate}
                          disabled={!newUpdateText.trim() || postingUpdate}
                        >
                          {postingUpdate ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Posting...
                            </>
                          ) : (
                            <>
                              <MessageCircle className="w-4 h-4 mr-2" />
                              Post Update
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Underwriting Workflow */}
                  <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-emerald-600" />
                        Underwriting Assessment
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Two-Stage Process */}
                      <div className="grid grid-cols-2 gap-3">
                        {/* Stage 1: Filtering */}
                        <div className="border rounded-lg p-3 bg-white">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-muted-foreground">STAGE 1: FILTERING</span>
                            {filteringResult?.filtering_passed === true && (
                              <Badge className="bg-green-100 text-green-700 text-[10px]">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Passed
                              </Badge>
                            )}
                            {filteringResult?.filtering_passed === false && (
                              <Badge className="bg-red-100 text-red-700 text-[10px]">
                                <XCircle className="w-3 h-3 mr-1" />
                                Failed
                              </Badge>
                            )}
                          </div>
                          <p className="text-[10px] text-muted-foreground mb-2">
                            Quick checks: Digital footprint, Director network
                          </p>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="w-full justify-start text-xs"
                            onClick={handleRunFiltering}
                            disabled={underwritingLoading}
                            data-testid="run-filtering-btn"
                          >
                            {underwritingLoading ? (
                              <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                            ) : (
                              <PlayCircle className="w-3 h-3 mr-2" />
                            )}
                            Run Filtering Checks
                          </Button>
                          
                          {/* Filtering Results */}
                          {filteringResult && (
                            <div className="mt-2 pt-2 border-t space-y-1.5">
                              {filteringResult.warning_flags?.length > 0 && (
                                <div className="flex items-start gap-1.5 text-[10px] text-yellow-700 bg-yellow-50 p-1.5 rounded">
                                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  <span>{filteringResult.warning_flags.join(", ")}</span>
                                </div>
                              )}
                              {filteringResult.decline_reasons?.length > 0 && (
                                <div className="flex items-start gap-1.5 text-[10px] text-red-700 bg-red-50 p-1.5 rounded">
                                  <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  <span>{filteringResult.decline_reasons.join(", ")}</span>
                                </div>
                              )}
                              {filteringResult.filtering_passed && filteringResult.decline_reasons?.length === 0 && (
                                <div className="flex items-start gap-1.5 text-[10px] text-green-700 bg-green-50 p-1.5 rounded">
                                  <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  <span>No major issues found</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Stage 2: Full Assessment */}
                        <div className="border rounded-lg p-3 bg-white">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-muted-foreground">STAGE 2: ASSESSMENT</span>
                            {assessmentResult?.decision && (
                              <Badge className={`text-[10px] ${
                                assessmentResult.decision === 'APPROVED' ? 'bg-green-100 text-green-700' :
                                assessmentResult.decision === 'CONDITIONAL' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {assessmentResult.decision === 'APPROVED' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                                {assessmentResult.decision === 'DECLINED' && <XCircle className="w-3 h-3 mr-1" />}
                                {assessmentResult.decision === 'CONDITIONAL' && <AlertTriangle className="w-3 h-3 mr-1" />}
                                {assessmentResult.decision}
                              </Badge>
                            )}
                          </div>
                          <p className="text-[10px] text-muted-foreground mb-2">
                            Risk scoring & affordability analysis
                          </p>
                          <Button 
                            size="sm" 
                            variant={filteringResult?.filtering_passed ? "default" : "outline"}
                            className="w-full justify-start text-xs"
                            onClick={handleRunFullAssessment}
                            disabled={underwritingLoading || !filteringResult?.filtering_passed}
                            data-testid="run-assessment-btn"
                          >
                            {underwritingLoading ? (
                              <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                            ) : (
                              <Zap className="w-3 h-3 mr-2" />
                            )}
                            Run Full Assessment
                          </Button>
                          
                          {/* Assessment Results */}
                          {assessmentResult && (
                            <div className="mt-2 pt-2 border-t space-y-1.5">
                              <div className="flex justify-between text-[10px]">
                                <span className="text-muted-foreground">Risk Factor:</span>
                                <span className={`font-semibold ${
                                  assessmentResult.overall_risk_factor <= 1.5 ? 'text-green-600' :
                                  assessmentResult.overall_risk_factor <= 2 ? 'text-yellow-600' : 'text-red-600'
                                }`}>
                                  {assessmentResult.overall_risk_factor}
                                </span>
                              </div>
                              {assessmentResult.recommended_loan_amount && (
                                <div className="flex justify-between text-[10px]">
                                  <span className="text-muted-foreground">Recommended:</span>
                                  <span className="font-semibold">£{assessmentResult.recommended_loan_amount?.toLocaleString()}</span>
                                </div>
                              )}
                              {assessmentResult.conditions?.length > 0 && (
                                <div className="text-[10px] text-yellow-700 bg-yellow-50 p-1.5 rounded">
                                  <strong>Conditions:</strong> {assessmentResult.conditions.join(", ")}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Individual Check Buttons */}
                      <div className="border-t pt-3">
                        <p className="text-[10px] font-semibold text-muted-foreground mb-2">INDIVIDUAL CHECKS</p>
                        <div className="grid grid-cols-2 gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="justify-start text-xs"
                            onClick={handleCheckDigitalFootprint}
                            disabled={underwritingLoading}
                            data-testid="check-footprint-btn"
                          >
                            <Globe className="w-3 h-3 mr-2" />
                            Digital Footprint
                            {digitalFootprintResult && !digitalFootprintResult.error && (
                              <span className="ml-auto text-[10px] text-emerald-600">
                                {digitalFootprintResult.reputation_score}/5
                              </span>
                            )}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="justify-start text-xs"
                            onClick={handleCheckDirectorNetwork}
                            disabled={underwritingLoading}
                            data-testid="check-director-btn"
                          >
                            <UserCheck className="w-3 h-3 mr-2" />
                            Director Network
                            {directorNetworkResult && !directorNetworkResult.error && (
                              <span className={`ml-auto text-[10px] ${
                                directorNetworkResult.insolvency_flags?.length > 0 ? 'text-red-600' : 'text-green-600'
                              }`}>
                                {directorNetworkResult.insolvency_flags?.length || 0} flags
                              </span>
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Digital Footprint Details */}
                      {digitalFootprintResult && !digitalFootprintResult.error && (
                        <div className="border rounded-lg p-3 bg-slate-50 text-xs">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold">Digital Footprint Results</span>
                            <Badge variant="outline" className="text-[10px]">
                              Score: {digitalFootprintResult.reputation_score}/5
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-[10px]">
                            <div className="bg-green-50 p-2 rounded text-center">
                              <div className="font-semibold text-green-700">{digitalFootprintResult.review_indicators?.positive_signals || 0}</div>
                              <div className="text-muted-foreground">Positive</div>
                            </div>
                            <div className="bg-slate-100 p-2 rounded text-center">
                              <div className="font-semibold">{digitalFootprintResult.review_indicators?.neutral_signals || 0}</div>
                              <div className="text-muted-foreground">Neutral</div>
                            </div>
                            <div className="bg-red-50 p-2 rounded text-center">
                              <div className="font-semibold text-red-700">{digitalFootprintResult.review_indicators?.negative_signals || 0}</div>
                              <div className="text-muted-foreground">Negative</div>
                            </div>
                          </div>
                          {digitalFootprintResult.trustpilot?.trustpilot_found && (
                            <div className="mt-2 pt-2 border-t flex items-center gap-2">
                              <span className="text-muted-foreground">Trustpilot:</span>
                              <a 
                                href={digitalFootprintResult.trustpilot.trustpilot_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:underline flex items-center gap-1"
                              >
                                {digitalFootprintResult.trustpilot.rating ? `${digitalFootprintResult.trustpilot.rating}/5` : "View"}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Director Network Details */}
                      {directorNetworkResult && !directorNetworkResult.error && (
                        <div className="border rounded-lg p-3 bg-slate-50 text-xs">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold">Director Network: {directorNetworkResult.director_name}</span>
                            {directorNetworkResult.insolvency_flags?.length > 0 ? (
                              <Badge className="bg-red-100 text-red-700 text-[10px]">
                                {directorNetworkResult.insolvency_flags.length} Insolvencies
                              </Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-700 text-[10px]">
                                Clear
                              </Badge>
                            )}
                          </div>
                          {directorNetworkResult.insolvency_flags?.length > 0 && (
                            <div className="space-y-1 mt-2">
                              {directorNetworkResult.insolvency_flags.map((flag, idx) => (
                                <div key={idx} className="bg-red-50 p-2 rounded text-[10px] text-red-700">
                                  <strong>{flag.company}</strong> - {flag.status} ({flag.role})
                                </div>
                              ))}
                            </div>
                          )}
                          {directorNetworkResult.risk_indicators?.length > 0 && (
                            <div className="mt-2 pt-2 border-t">
                              <span className="text-muted-foreground">Risk Indicators:</span>
                              <ul className="list-disc list-inside text-[10px] mt-1">
                                {directorNetworkResult.risk_indicators.map((ind, idx) => (
                                  <li key={idx}>{ind}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="updates" className="m-0 space-y-4 pr-2">
                  {updatesLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                      <span className="ml-2 text-muted-foreground">Loading updates...</span>
                    </div>
                  ) : itemUpdates.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No updates found for this item</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {itemUpdates.map((update) => (
                        <Card key={update.id} className="overflow-hidden">
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {update.creator?.photo ? (
                                  <img 
                                    src={update.creator.photo} 
                                    alt={update.creator.name}
                                    className="w-6 h-6 rounded-full"
                                  />
                                ) : (
                                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                                    <User className="w-3 h-3 text-emerald-600" />
                                  </div>
                                )}
                                <span className="font-medium text-sm">{update.creator?.name || "Unknown"}</span>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {update.created_at ? new Date(update.created_at).toLocaleString("en-GB") : ""}
                              </span>
                            </div>
                            <div 
                              className="text-sm prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: update.body || update.text_body || "" }}
                            />
                            {update.assets?.length > 0 && (
                              <div className="mt-2 pt-2 border-t flex flex-wrap gap-2">
                                {update.assets.map((asset) => (
                                  <a
                                    key={asset.id}
                                    href={asset.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded flex items-center gap-1"
                                  >
                                    <Paperclip className="w-3 h-3" />
                                    {asset.name}
                                  </a>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </div>
              
              {/* Footer Actions */}
              <div className="flex gap-2 pt-4 border-t mt-4 flex-shrink-0">
                {selectedItem.monday_item_id && selectedItem.monday_board_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => {
                      window.open(`https://simplyfunded.monday.com/boards/${selectedItem.monday_board_id}/pulses/${selectedItem.monday_item_id}`, "_blank");
                    }}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open in Monday.com
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => setShowItemModal(false)}>
                  Close
                </Button>
              </div>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
